/**
 * Persist critical game state to localStorage.
 * Survives browser crashes and page discards during live quests.
 * Restored on page load if gameCode present.
 */

import type { GameState } from '../store';

const STORAGE_KEY = 'autoquest_game_state';

/** Fields worth persisting (not UI state, not leaderboard) */
const PERSISTED_KEYS: (keyof GameState)[] = [
  'gameCode', 'gameTitle', 'playerId', 'playerName', 'playerRole',
  'isCaptain', 'teamName', 'token', 'playerToken', 'organizerPhone', 'fullOffline',
  'allowManualSkip',
  'status', 'locations', 'currentLocationIndex',
  'locationStartedAt', 'gameStartedAt',
  'salt', 'firstRiddleKey', 'nextKeys',
  'currentRiddleText', 'currentHintText',
  'allowTeamChat', 'canChat', 'completedAt', 'totalTimeMs', 'wrongAnswers', 'penaltyMs',
  'lobbyChatMode', 'gameRules', 'demoSpeedMultiplier',
  // Session 53 post-ship — persist the per-location UI-phase state so F5
  // on the site-code input screen (user verified geo, now entering codes)
  // restores to site-code, not back to the coord/address entry step.
  // User-reported bug 2026-04-18: "F5 на странице ввода кода выкидывает
  // в логин экран". Without these, `geoVerified`/`hintNavigateData` are
  // undefined after restore and `renderLocationView` falls back to the
  // 'input' phase, rewinding the player's progress through the location.
  'geoVerified', 'hintNavigateData',
];

export function persistState(state: GameState): void {
  if (!state.gameCode) return; // nothing to persist
  try {
    const subset: Record<string, unknown> = {};
    for (const key of PERSISTED_KEYS) {
      subset[key] = state[key];
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subset));
  } catch {
    // localStorage full or unavailable — silently degrade
  }
}

export function loadPersistedState(): Partial<GameState> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.gameCode || !parsed.playerId) return null;
    return parsed as Partial<GameState>;
  } catch {
    return null;
  }
}

/**
 * Clears ONLY the persisted game-state blob. Use when resetting in-memory
 * game state (called from `resetState` in store.ts). Leaves auxiliary keys
 * (`aq_errors`, `aq_demo_join`) untouched so error history and one-shot
 * demo handoffs survive.
 *
 * Session 69 rename: was `clearPersistedState`; renamed so the distinction
 * between game-state-only vs all-PWA-data is explicit at every call site.
 */
export function clearGameState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * @deprecated Use `clearGameState`. Kept as a thin alias so any extension
 * modules (or older commits during rebase) keep working. Inside this repo
 * every call site is migrated to `clearGameState` in session 69.
 */
export const clearPersistedState = clearGameState;

/**
 * Session 69 — hard wipe of all AutoQuest PWA localStorage on this origin.
 * Called from (a) `results-view.ts` «Новая игра» handler, (b) `main.ts`
 * boot guard when a stale FINISHED snapshot is detected. Removes:
 *   - `autoquest_game_state` (persisted GameState)
 *   - `aq_errors` (rolling error log — may contain stack traces with user
 *     nicknames, so we purge on explicit fresh-start)
 *   - `aq_demo_join` (one-shot demo handoff — if still present here it
 *     was never consumed, so it's stale anyway)
 * `aq_draft_handoff` (dashboard scroll-restore) is not on the PWA origin,
 * so we don't touch it.
 */
export function clearAllGameData(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('aq_errors');
    localStorage.removeItem('aq_demo_join');
    // v0.79.18 — also wipe offline answer queue. Keys are
    // `aq_sync_queue_<gameCode>` (see api/offline-queue.ts). On a shared
    // device, leaving the prior player's queued plaintext answers behind is
    // a data-residue privacy issue.
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('aq_sync_queue_')) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable — silently degrade
  }
  // v0.79.30 (Bug 4) — auth-failure guards are NOT reset here anymore.
  // Pre-fix, resetting them inside the handler-invoked clearAllGameData
  // created a spam loop where every in-flight 401 (chat-poll, lobby history,
  // WS close-1008) re-fired the handler and showed a fresh toast.
  // Guards are now armed by `armAuthFailureHandler()` from the join-success
  // path, which is where a fresh session actually starts.
}
